import { useEffect, useState } from "react";
import { useParams, Link } from "react-router";
import { getCreation, ApiError, type Creation } from "../lib/api.ts";

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; creation: Creation };

export default function CreationScroll() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setState({ status: "loading" });
    getCreation(slug)
      .then((creation) => {
        if (!cancelled) setState({ status: "ready", creation });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message:
            err instanceof ApiError ? err.message : "Could not fetch creation",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state.status === "loading") {
    return (
      <div className="scroll-stage flex items-center justify-center">
        <p className="font-serif text-xl italic text-[var(--color-cream)]/70">
          unrolling your scroll…
        </p>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="scroll-stage flex flex-col items-center justify-center gap-6 text-center">
        <p className="font-serif text-2xl italic text-[var(--color-cream)]/80">
          This scroll could not be fetched.
        </p>
        <p className="text-sm text-[var(--color-cream)]/60">{state.message}</p>
        <Link
          to="/create"
          className="text-[0.7rem] uppercase tracking-[0.2em] text-[var(--color-gold-soft)] hover:text-[var(--color-gold)]"
        >
          ← compose a candle
        </Link>
      </div>
    );
  }

  const c = state.creation;
  const created = new Date(c.createdAt * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="scroll-stage">
      {/* Print chrome — hidden when printing */}
      <div className="print-hide mx-auto mb-10 flex max-w-3xl items-center justify-between text-[var(--color-cream)]/75">
        <Link
          to={`/creation/${slug}`}
          className="text-[0.7rem] uppercase tracking-[0.2em] hover:text-[var(--color-gold-soft)]"
        >
          ← back to candle
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className="border border-[var(--color-gold-soft)]/60 px-6 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] text-[var(--color-gold-soft)] transition-colors duration-300 hover:border-[var(--color-gold)] hover:text-[var(--color-gold)]"
        >
          Print This Scroll
        </button>
      </div>

      <article className="scroll-paper">
        <div className="scroll-frame">
          <header className="text-center">
            <div className="scroll-seal" aria-hidden>
              B
            </div>
            <p className="scroll-eyebrow">BeAdoré · Maison de Bougies</p>
            <h1 className="scroll-heading mt-4 text-5xl leading-[1.05] md:text-6xl">
              {c.candleName}
            </h1>
            <p className="mt-3 font-serif text-lg italic text-[#5a3a0a]/80">
              composed for <em>{c.userName}</em> · born under {c.zodiacSign}
            </p>
            <div className="scroll-rule mt-6" />
          </header>

          <section className="mt-10">
            <p className="scroll-eyebrow text-center">Your Reading</p>
            <p className="mt-5 font-serif text-[1.2rem] leading-[1.85] text-[#2a1d05] first-letter:float-left first-letter:pr-2 first-letter:font-serif first-letter:text-5xl first-letter:leading-[0.9] first-letter:text-[#7a1f12] md:text-[1.32rem]">
              {c.reading}
            </p>
          </section>

          <div className="scroll-rule mt-12" />

          <section className="mt-10">
            <p className="scroll-eyebrow text-center">Scent Story</p>
            <ul className="mx-auto mt-7 max-w-md space-y-5">
              <ScrollScent label="Top" value={c.scent.top} />
              <ScrollScent label="Heart" value={c.scent.heart} />
              <ScrollScent label="Base" value={c.scent.base} />
            </ul>
          </section>

          <div className="scroll-rule mt-12" />

          <section className="mt-10 text-center">
            <p className="scroll-eyebrow">Inscribed for the Jar</p>
            <p className="mt-5 font-serif text-[1.65rem] italic leading-snug text-[#2a1d05] md:text-[1.9rem]">
              “{c.dedication}”
            </p>
          </section>

          <footer className="mt-14 text-center">
            <p className="font-serif text-xs uppercase tracking-[0.24em] text-[#5a3a0a]/70">
              Composed on {created}
            </p>
            <p className="mt-2 font-serif text-sm italic text-[#5a3a0a]/70">
              by the atelier at BeAdoré
            </p>
          </footer>
        </div>
      </article>

      <p className="print-hide mx-auto mt-10 max-w-md text-center text-xs italic text-[var(--color-cream)]/55">
        Tip: print on ivory or kraft stock for the truest old-world feel.
      </p>
    </div>
  );
}

function ScrollScent({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex items-baseline gap-6 border-b border-[rgba(90,54,10,0.3)] pb-3 last:border-b-0">
      <span className="w-14 shrink-0 text-[0.62rem] font-semibold uppercase tracking-[0.24em] text-[#7a1f12]">
        {label}
      </span>
      <span className="font-serif text-lg italic leading-snug text-[#2a1d05]">
        {value}
      </span>
    </li>
  );
}
